import React from "react"
import {
  Badge, Box, Flex, Heading, Text,
} from "@chakra-ui/react"
import { WhatsAppTemplate } from "sharedTypes"
import PaperclipIcon from "components/TemplateEditor/PaperclipIcon"
import OptInMsgIcon from "components/elements/OptInMsgIcon"
import { joinPunctuation, parseTemplatePreview } from "utilities/whatsappTemplates"
import VariablePill from "components/TemplateEditor/VariablePill"

const MEDIA_TYPE_LABELS: Record<string, string> = {
  image: "Image",
  document: "Document",
}

const getAttachmentLabel = (template: WhatsAppTemplate): string | null => {
  const userMedia = template.variables
    .find((v, i) => v.type === "media" && !template.preview.includes(`{{${i + 1}}}`))
  if (userMedia) {
    const typeLabel = userMedia.mediaType ? MEDIA_TYPE_LABELS[userMedia.mediaType] : null

    return typeLabel ? `Attachment: ${typeLabel}` : "Attachment"
  }

  const hasQRCode = template.variables
    .some((v) => v.type === "locked" && v.mergeTag === "GUEST_QR_CODE_IMAGE")
  if (hasQRCode) return "Attachment: QR Code"

  return null
}

const CATEGORY_COLORS: Record<string, string> = {
  utility: "whatsapp",
  marketing: "orange",
}

type Props = {
  template: WhatsAppTemplate
  selected: boolean
  onClick: () => void
  channel: "sms" | "whatsapp"
}

const TextTemplateCard = ({
  template, selected, onClick, channel,
}: Props) => {
  const segments = parseTemplatePreview(template.preview, template.variables)
  const attachmentLabel = getAttachmentLabel(template)

  return (
    <Box
      onClick={onClick}
      cursor="pointer"
      borderWidth={3}
      borderColor={selected ? "blue.500" : "transparent"}
      borderRadius="xl"
      transition="all 0.2s"
      _hover={{
        borderColor: selected ? "blue.600" : "blue.200",
      }}
    >
      <Box
        bg={selected ? "blue.50" : "gray.25"}
        borderWidth={1}
        borderColor="gray.200"
        borderRadius="lg"
        p={4}
        h="full"
      >
        <Flex justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Heading fontSize="md" fontWeight="semibold" flex={1}>
            {template.name}
          </Heading>
          {channel === "whatsapp" && (
            <Flex alignItems="center" gap={1.5} ml={2} flexShrink={0}>
              {template.messageType === "opt_in" && (
                <OptInMsgIcon boxSize={4} color="gray.500" />
              )}
              <Badge
                variant="outline"
                colorScheme={CATEGORY_COLORS[template.category] ?? "gray"}
                borderRadius="full"
                fontSize="2xs"
                textTransform="uppercase"
                fontWeight="semibold"
                px={1.5}
                py={0.25}
              >
                {template.category}
              </Badge>
            </Flex>
          )}
        </Flex>
        <Text fontSize="sm" color="gray.800" mb={3}>
          {template.description}
        </Text>
        <Box
          borderWidth={1}
          borderColor="gray.200"
          borderRadius="md"
          overflow="hidden"
          bg="white"
        >
          <Box px={3} py={1.5} borderBottomWidth={1} borderColor="gray.200">
            <Text fontSize="xs" color="gray.500">Preview</Text>
          </Box>
          <Box p={3} fontSize="xs" color="gray.800" lineHeight="taller" whiteSpace="pre-wrap">
            {segments.map((segment, i) => {
              if (segment.type === "text") {
                return <Text as="span" key={`text-${i}`}>{joinPunctuation(segment.content, i)}</Text>
              }

              return (
                <VariablePill
                  key={`var-${segment.index}`}
                  variable={segment.variable}
                  name=""
                  readOnly
                />
              )
            })}
            {channel === "whatsapp" && attachmentLabel && (
              <Flex alignItems="center" gap={1} mt={2} color="gray.500">
                <PaperclipIcon size={3} />
                <Text fontSize="xs">{attachmentLabel}</Text>
              </Flex>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default TextTemplateCard
